/// Tower Log — public stats data layer. The counters themselves come from
/// the `getTowerLog` Cloud Function (a plain public JSON GET, no auth, no
/// Firestore reads from the client — see functions/src/towerLog.ts), reached
/// same-origin via the Hosting rewrite `/api/towerlog`. The weekly-recap /
/// milestone ENTRIES are separate: they're ordinary `webDevPosts` docs
/// (`category: "towerlog"`), read straight from Firestore via
/// `subscribeTowerLogPosts` in `devBlog.ts` so they get the board's existing
/// votes/comments/notifications for free.

const ENDPOINT = '/api/towerlog'

export type CounterValue = number | 'coming_soon'

export interface TowerLogComingSoonBadge {
  id: string
  label: string
}

/// Per-counter movement since the last weekly baseline, from the server's
/// `buildWeeklyChange`. Counters that did not move are ABSENT, not zero — the
/// renderer's rule is a plain absence check. `since` is a UTC `YYYY-MM-DD`
/// Monday.
export interface TowerLogWeeklyChange {
  since: string
  deltas: Record<string, number>
}

export interface TowerLogPayload {
  available: boolean
  reason?: string
  updatedAt?: string | null
  counters?: Record<string, CounterValue>
  labels?: Record<string, string>
  comingSoon?: TowerLogComingSoonBadge[]
  weeklyChange?: TowerLogWeeklyChange | null
}

/// "Mon, Jul 27" for the grid's shared change-since header.
///
/// ⚠ `new Date('2026-07-27')` parses as UTC midnight, so formatting it in any
/// timezone west of Greenwich renders the day BEFORE — the header would have
/// claimed Sunday for every US visitor. Formatting back in UTC is what keeps
/// the rendered day equal to the weekKey the server actually sent.
export function formatChangeSince(since: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(since)) return null
  const d = new Date(`${since}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' })
}

/// The tile sub-line, or null when this counter has not moved (or the log has
/// no baseline yet). Deliberately renders a bare "+N" and leaves the window
/// wording to the one shared header.
export function formatCounterDelta(
  weeklyChange: TowerLogWeeklyChange | null | undefined,
  counterId: string,
): string | null {
  const delta = weeklyChange?.deltas?.[counterId]
  if (typeof delta !== 'number' || !Number.isFinite(delta) || delta <= 0) return null
  return `+${Math.round(delta).toLocaleString('en-US')}`
}

/// Which "coming soon" badges still deserve their OWN tile.
///
/// The `comingSoon` list exists for entries that are not real counters at
/// all. A counter that merely has no data yet already renders its own tile
/// from `counters` (its value is the `'coming_soon'` sentinel), so listing it
/// in both places drew the same chip twice — exactly what happened when
/// `conflictsSurvived` graduated from a badge-only entry into a real counter
/// on 2026-07-20. Filtering here (rather than trusting the server to send a
/// disjoint list) keeps the page correct against any payload shape.
export function visibleComingSoonBadges(
  comingSoon: readonly TowerLogComingSoonBadge[] | undefined,
  counters: Record<string, CounterValue> | undefined,
): TowerLogComingSoonBadge[] {
  if (!comingSoon || comingSoon.length === 0) return []
  const owned = new Set(Object.keys(counters ?? {}))
  return comingSoon.filter((b) => !owned.has(b.id))
}

/// One-shot fetch (the page also polls this occasionally — counters change
/// at most once a day, so there's no need for anything fancier than a plain
/// GET with the function's own `Cache-Control: max-age=300`).
export async function fetchTowerLog(): Promise<TowerLogPayload> {
  const res = await fetch(ENDPOINT, { signal: AbortSignal.timeout(10_000) })
  if (!res.ok) {
    throw new Error(`getTowerLog HTTP ${res.status}`)
  }
  return (await res.json()) as TowerLogPayload
}
